<script setup>
    import { makeHash } from '~/composables/makeHash';
    import { storeToRefs } from 'pinia';
    import { useNeighborhoodsStore } from '@/stores/neighborhoods';
    const { neighborhoodsData } = storeToRefs(useNeighborhoodsStore());
    const neighborhoods = ref([]);
    neighborhoods.value = neighborhoodsData.value || [];

    const isMapShown = ref(false);
    const route = useRoute();
    
    const isIframe = computed(() => {
        return route.path.startsWith('/iframe');
    });

    function handleToggle(e) {
        isMapShown.value = e;
    }

    watch(neighborhoodsData, () => {
        neighborhoods.value = neighborhoodsData.value || [];
    });

    // Calculate distance between title-map-list-toggle bottom and bottom-navigation top
    const hdContainerRef = ref(null);
    const distanceToBottomNav = ref(0);

    // Same unbounded-retry shape as Blocks/map.vue, and dead for the same reason
    // until the Nuxt 4 migration: the guard below reads `!import.meta.client`,
    // which was `!process.client` — a falsy constant after the Nuxt 4 upgrade, so
    // this returned on every call.
    //
    // This page does render BlocksTitleMapListToggle, so the element is present
    // and the retry resolves. Bounded anyway: an unbounded retry that only works
    // because a sibling component happens to render is one refactor away from
    // pinning a core, which is exactly how map.vue's copy survived.
    const MAX_TOGGLE_RETRIES = 20;
    let toggleRetries = 0;
    let pendingRetry = null;

    const calculateDistanceToBottomNav = () => {
        if (!import.meta.client) return;

        const titleToggle = document.getElementById('title-map-list-toggle');
        const bottomNav = document.getElementById('bottom-navigation');
        const footer = document.getElementById('footer-holder');
        
        if (!titleToggle) {
            // Retry only while the toggle itself is still mounting, then give up.
            // Retrying on a missing bottom nav had no bail-out, and the nav is now
            // gone for good (Asana 1217333501985924), so that would have looped
            // forever.
            if (toggleRetries >= MAX_TOGGLE_RETRIES) return;
            toggleRetries++;
            nextTick(() => {
                pendingRetry = setTimeout(calculateDistanceToBottomNav, 0);
            });
            return;
        }

        toggleRetries = 0;

        const titleToggleRect = titleToggle.getBoundingClientRect();
        const anchor = bottomNav || footer;
        const bottomNavRect = anchor ? anchor.getBoundingClientRect() : { top: window.innerHeight };
        
        // Calculate from bottom of title-map-list-toggle to top of bottom-navigation
        distanceToBottomNav.value = bottomNavRect.top - titleToggleRect.bottom - 20;
    };

    onMounted(() => {
        // Initial calculation with delay to ensure elements are loaded
        nextTick(() => {
            setTimeout(calculateDistanceToBottomNav, 0);
        });
        window.addEventListener('resize', calculateDistanceToBottomNav);
    });

    onUnmounted(() => {
        window.removeEventListener('resize', calculateDistanceToBottomNav);
        if (pendingRetry) {
            clearTimeout(pendingRetry);
            pendingRetry = null;
        }
    });

    // CSS variable for distance
    const distanceCssVar = computed(() => {
        return isIframe.value ? 'auto' : `${distanceToBottomNav.value}px`;
    });

</script>

<template>
    <div class="neighborhoods-landing container" :class="{ 'is-map': isMapShown }">
        <BlocksTitleMapListToggle @viewToggle="handleToggle" title="Neighborhood Areas" :mapView="isMapShown" />
        <div class="hd-container" ref="hdContainerRef" :style="`--distance-to-bottom-nav: ${distanceCssVar}`">
            <BlocksSearchBarWithList :key="makeHash(neighborhoods)" v-if="!isMapShown" :items="neighborhoods" :isMapShown="isMapShown" />
            <!-- Christine asked for last year's downloadable map back, without the
                 interactive cards underneath (Asana O2-73). BlocksSvgMapWithCarousel
                 is left in place, unused, so this is a one-line reversal. -->
            <BlocksSvgMapWithDownload v-if="isMapShown" :items="neighborhoods" :isMapShown="isMapShown" />
        </div>
    </div>
</template>

<style lang="scss">
    .neighborhoods-landing {

        .carousel-component .carousel-inner .carousel-item.is-full-width {
            align-self: flex-end;
        }

        .title-map-list-toggle.component-header {
            padding: 20px 16px;

            // See Templates/Sites/page.vue — $hd + the two gutters, or the
            // title sits 5px from the window edge between 1150 and 1182.
            @media (min-width: $hd + 32px) {
                padding: 20px 0px;
            }
        }

        @media (min-width: $tablet) {
            
            max-width: $hd;
            width: 100vw;
            margin: auto;
            min-height: 60vh;
            
            &.is-map {
                margin-bottom: 0px;
            }

            .is-iframe & {
                margin-bottom: 0;
            }

            .is-iframe &.is-map {
                margin-bottom: 32px;
            }            

            .carousel-component .carousel-inner .carousel-item.is-full-width {
                align-self: stretch;
            }
        }
    }
</style>
